import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { ShowSnackbar } from './../../shared/shared.actions'
import {
  LoginFailure,
  LoginRequest,
  LoginSuccess,
  LogoutFailure,
  LogoutRequest,
  LogoutSuccess,
  PasswordResetFailure,
  PasswordResetRequest,
  PasswordResetSuccess,
  RequestPasswordResetFailure,
  RequestPasswordResetRequest,
  RequestPasswordResetSuccess,
  TwoFactorVerificationCodeSent,
} from './auth.actions'
import { AuthService } from './auth.service'
import * as AuthTypes from './auth.types'

@Injectable()
export class AuthEffects {
  constructor(
    private actions: Actions,
    private authService: AuthService,
    private router: Router,
  ) {}

  Logout$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthTypes.LOGOUT_REQUEST),
      map((action: LogoutRequest) => action.redirect),
      switchMap((redirect) => {
        return this.authService.logout().pipe(
          map(() => {
            if (redirect) {
              this.router.navigate(['/auth/login'])
            }

            return new LogoutSuccess()
          }),
          catchError((err, caught) => {
            return of(new LogoutFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  Login$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthTypes.LOGIN_REQUEST),
      map((action: LoginRequest) => action.payload),
      switchMap((payload) => {
        return this.authService.login(payload).pipe(
          mergeMap(({ data }) => {
            if (data.token) {
              return [new LoginSuccess(data)]
            } else if (data.twoFactorVerificationCodeSent) {
              return [new TwoFactorVerificationCodeSent(data)]
            }

            return [
              new LoginFailure({ error: 'API error' }),
              new ShowSnackbar({
                type: 'error',
                message: 'Something went wrong :(',
              }),
            ]
          }),
          catchError((err, caught) => {
            return of(new LoginFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  LogInSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(AuthTypes.LOGIN_SUCCESS),
        tap(({ payload }: any) => {
          this.authService.setUser(payload.token)
          this.router.navigate(['/'])
        }),
      ),
    { dispatch: false },
  )

  RequestPasswordReset$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthTypes.REQUEST_PASSWORD_RESET_REQUEST),
      map((action: RequestPasswordResetRequest) => action.payload),
      switchMap((payload) => {
        return this.authService.requestPasswordReset(payload['email']).pipe(
          mergeMap(({ data }) => {
            return [new RequestPasswordResetSuccess(data)]
          }),
          catchError((err, caught) => {
            return of(new RequestPasswordResetFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  PasswordReset$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthTypes.PASSWORD_RESET_REQUEST),
      map((action: PasswordResetRequest) => action.payload),
      switchMap((payload) => {
        return this.authService.passwordReset(payload).pipe(
          mergeMap(({ data }) => {
            return [new PasswordResetSuccess(data)]
          }),
          catchError((err, caught) => {
            return of(new PasswordResetFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  PasswordResetSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(AuthTypes.PASSWORD_RESET_SUCCESS),
        tap(({ payload }) => {
          this.router.navigate(['/auth/login'])
        }),
      ),
    { dispatch: false },
  )
}
